import {useCallback} from 'react';
import {ReactFlowInstance} from 'reactflow';

import {assert} from 'Asserts';
import {FlowchartConfigMetadataWithRootTaskSchema} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartAdapter';
import {ProcessesPluginBase} from 'processes/graph-builder-processes/platform/ProcessesPluginBase';
import legacyRunModal from 'ui-library/legacyRunModal';
import {FlowchartSidepanelManagerContextValue} from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelUnsavedChangesContext';

import FlowchartRemoveNodeModal from '../../../../../ui-library/reactflow/nodes/modals/FlowchartRemoveNodeModal';
import {isNodeDeletable} from '../adapterUtils';
import {FlowchartEdge} from '../processFlowchartConfigEditorConstants';
import {
  FlowchartNode,
  isInteractiveTaskNode,
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartEdgeChange, FlowchartNodeChange, getRootAdapter} from '../processFlowchartConfigEditorUtils';
import {FlowchartConfigMetadata} from '../redux/reduceFlowchartConfig';
import {DeletedNodeMetadata, trackNodeRemovalAnalytics} from './useFlowchartStateAnalytics';

interface UseNodeDeletionParams {
  locked: boolean;
  allowStartEndNodeDeletion: boolean;
  plugin: ProcessesPluginBase;
  flowchartConfigMetadata: FlowchartConfigMetadata;
  staticNodesData: Array<StaticFlowchartNodeData>;
  flowchartTypeName: string;
  reactFlowInstance?: ReactFlowInstance;
  sidepanelManager: FlowchartSidepanelManagerContextValue;
  onReactFlowNodeChangesOnly: (nodeChanges: Array<FlowchartNodeChange>) => void;
  onReactFlowEdgeChangesOnly: (edgeChanges: Array<FlowchartEdgeChange>) => void;
}

/**
 * Provides the node-deletion entry points used by the delete key handler and
 * ReactFlow's onNodeDelete callback. Shows a confirmation modal when nodes are
 * removed, tracks analytics, and closes the sidepanel.
 */
export function useNodeDeletion({
  locked,
  allowStartEndNodeDeletion,
  plugin,
  flowchartConfigMetadata,
  staticNodesData,
  flowchartTypeName,
  reactFlowInstance,
  sidepanelManager,
  onReactFlowNodeChangesOnly,
  onReactFlowEdgeChangesOnly,
}: UseNodeDeletionParams): {
  deleteNodesWithConfirmation: (
    nodesToDelete: Array<FlowchartNode>,
    edgesToDelete?: Array<FlowchartEdge>
  ) => Promise<void>;
  onNodeDelete: (nodeId: string) => void;
} {
  const deleteNodesWithConfirmation = useCallback(
    async (nodesToDelete: Array<FlowchartNode>, edgesToDelete: Array<FlowchartEdge> = []): Promise<void> => {
      const deletableNodes = nodesToDelete.filter((node) =>
        isNodeDeletable(node, {allowStartEndNodeDeletion})
      );

      if (deletableNodes.length === 0) {
        return;
      }

      const nodeNames = deletableNodes.map((node) => node.data.nodeName);

      const edgeChanges = edgesToDelete.map((edge) => ({
        type: 'remove' as const,
        id: edge.id,
      }));

      const nodeChanges = deletableNodes.map((node) => ({
        type: 'remove' as const,
        id: node.id,
      }));

      const rootAdapter = getRootAdapter(plugin);
      assert(
        rootAdapter != null && flowchartConfigMetadata.rootTaskSchema != null,
        'Expected rootAdapter and rootTaskSchema to be defined'
      );

      const confirmationContent = rootAdapter.getRemoveNodeConfirmationContent(
        nodeNames.length === 1
          ? {
              isMultiNodeRemoval: false as const,
              nodeName: nodeNames[0],
              metadata: flowchartConfigMetadata as FlowchartConfigMetadataWithRootTaskSchema,
            }
          : {
              isMultiNodeRemoval: true as const,
              metadata: flowchartConfigMetadata as FlowchartConfigMetadataWithRootTaskSchema,
            }
      );

      const deletedNodesMetadata: Array<DeletedNodeMetadata> = deletableNodes.map((node) => {
        const isTaskNode = isInteractiveTaskNode(node);
        return {
          nodeName: node.data.nodeName,
          nodeType: node.data.nodeType,
          executionType: isTaskNode
            ? node.data.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
            : null,
          taskSchemaId: isTaskNode ? node.data.taskSchemaId : null,
        };
      });

      const taskSchemaIdsOnCanvas = staticNodesData
        .filter((node) => isStaticTaskSchemaNodeData(node))
        .map((node) => node.taskSchemaId);

      const previouslyFocusedElement = document.activeElement as HTMLElement | null;
      const result = await legacyRunModal(FlowchartRemoveNodeModal, {
        nodeNames,
        title: confirmationContent.title,
        subtitle: confirmationContent.subtitle,
        onRequestRemove: () => {
          trackNodeRemovalAnalytics(deletedNodesMetadata, taskSchemaIdsOnCanvas, flowchartTypeName);

          sidepanelManager.forceClose();

          if (edgeChanges.length > 0) {
            onReactFlowEdgeChangesOnly(edgeChanges);
          }

          onReactFlowNodeChangesOnly(nodeChanges);
        },
      });
      if (result == null) {
        // If canceling deletion, keep node focused to allow delete key again
        previouslyFocusedElement?.focus();
      }
    },
    [
      allowStartEndNodeDeletion,
      onReactFlowEdgeChangesOnly,
      onReactFlowNodeChangesOnly,
      sidepanelManager,
      plugin,
      flowchartConfigMetadata,
      staticNodesData,
      flowchartTypeName,
    ]
  );

  const onNodeDelete = useCallback(
    (nodeId: string): void => {
      if (locked) {
        return;
      }

      const node = reactFlowInstance?.getNode(nodeId) as FlowchartNode | undefined;
      if (node == null) {
        return;
      }

      deleteNodesWithConfirmation([node]);
    },
    [locked, reactFlowInstance, deleteNodesWithConfirmation]
  );

  return {deleteNodesWithConfirmation, onNodeDelete};
}
